import { parseTimeInMinutes } from './numberHelper'

/**
 * How an office handles the dates of a task (HAM-4286). Everything here is optional in the setting: a missing value
 * means "not configured" and {@link resolveTaskDateConfig} falls back to the behaviour of before HAM-4286, so an
 * office that never asked for anything notices no difference.
 */
export enum TaskDateField {
    Start = 'START',
    Due = 'DUE',
}

/** The name of the date on a task; also the field name in the DTO and the sort key of the overview. */
export type TaskDateName = 'start' | 'due'

export type TaskDateFieldConfiguration = {
    /** Whether the date takes part at all: an office without it never sees the field, in no screen. */
    enabled?: boolean
    /** Whether the date must be filled in before a task can be saved. */
    required?: boolean
    /** Whether a *new* task starts with today in this field. Editing an existing task never fills anything in. */
    prefillToday?: boolean
}

export type TaskDurationConfiguration = {
    /** Whether the office works with an expected duration at all. */
    enabled?: boolean
    /** Minimum duration as "h:mm". */
    minimum?: string
    /** The duration a new task starts with. Absent means the minimum, or 0:00 when there is no minimum. */
    initialValue?: string
    /** Whether a duration of 0:00 is left out of the overviews per task. */
    hideZeroValue?: boolean
}

export type TaskDateSettings = {
    startDate?: TaskDateFieldConfiguration
    dueDate?: TaskDateFieldConfiguration
    sortDate?: TaskDateField
    duration?: TaskDurationConfiguration
}

export type ResolvedTaskDateField = {
    /** Whether the field takes part at all (the office's `enabled`). A hidden date is never shown, asked or sorted on. */
    visible: boolean
    required: boolean
    /** Whether a new task starts with today in this field. */
    prefillToday: boolean
}

export type ResolvedTaskDateConfig = {
    /** Both dates take part; with one of them the field is labelled "Datum" instead of "Start datum"/"Tot datum". */
    twoDates: boolean
    start: ResolvedTaskDateField
    due: ResolvedTaskDateField
    /**
     * The date the overviews sort on by default; always a date that takes part. The date filter of the overviews
     * always works on the due date, whatever the office configured here.
     */
    sortField: TaskDateName
    /**
     * The expected duration: whether the office works with it, the minimum it allows, the value a new task starts on
     * and whether a duration of 0:00 is left out of the overviews.
     */
    duration: { enabled: boolean; minimum?: string; initialValue: string; hideZeroValue: boolean }
}

/** The behaviour of before HAM-4286, which is what an office without a configured date handling keeps. */
const defaults: ResolvedTaskDateConfig = {
    twoDates: true,
    start: { visible: true, required: false, prefillToday: true },
    due: { visible: true, required: true, prefillToday: true },
    sortField: 'due',
    duration: { enabled: false, initialValue: '0:00', hideZeroValue: false },
}

export function resolveTaskDateConfig(settings?: TaskDateSettings): ResolvedTaskDateConfig {
    const resolveField = (field?: TaskDateFieldConfiguration, fallback?: ResolvedTaskDateField): ResolvedTaskDateField => {
        const visible = field?.enabled ?? fallback.visible

        return {
            visible,
            // A date that does not take part is never required either, whatever the setting says.
            required: visible && (field?.required ?? fallback.required),
            prefillToday: field?.prefillToday ?? fallback.prefillToday,
        }
    }

    const start = resolveField(settings?.startDate, defaults.start)
    const due = resolveField(settings?.dueDate, defaults.due)
    const preferredSortField = settings?.sortDate === TaskDateField.Start ? 'start' : 'due'

    return {
        twoDates: start.visible && due.visible,
        start,
        due,
        // The sorting follows a date that takes part; with a single date there is nothing to choose.
        sortField: !start.visible ? 'due' : !due.visible ? 'start' : preferredSortField,
        duration: {
            enabled: settings?.duration?.enabled ?? defaults.duration.enabled,
            minimum: settings?.duration?.minimum || undefined,
            // A new task starts on the configured value, on the minimum when there is none, and never below it.
            initialValue: applyMinimumDuration(settings?.duration?.initialValue || settings?.duration?.minimum, settings?.duration?.minimum),
            hideZeroValue: settings?.duration?.hideZeroValue ?? defaults.duration.hideZeroValue,
        },
    }
}

export type TaskDates = {
    start?: string
    due?: string
}

/**
 * Set one of the dates and let the other one move along, so the due date never lies before the start date (HAM-4286,
 * criterion 8). A start date after the due date pushes the due date forward, a due date before the start date pulls
 * the start date back. An empty counterpart stays empty: there is nothing to move.
 *
 * The dates are ISO dates (YYYY-MM-DD), which compare correctly as plain strings.
 */
export function applyTaskDateShift(dates: TaskDates, field: TaskDateName, value?: string): TaskDates {
    const next: TaskDates = { ...dates, [field]: value || undefined }

    if (!next.start || !next.due || next.start <= next.due) {
        return next
    }

    return field === 'start' ? { ...next, due: next.start } : { ...next, start: next.due }
}

/**
 * The dates that the office requires but that this task does not have. Used by the overviews to mark a task as
 * incomplete (HAM-4286, criterion 20): existing tasks and workflow tasks can miss a date that only became required
 * later, and those tasks must stay visible and usable. An empty optional date is not a problem.
 */
export function getMissingTaskDates(dates: TaskDates, config: ResolvedTaskDateConfig): TaskDateName[] {
    const names: TaskDateName[] = ['start', 'due']

    return names.filter(name => config[name].visible && config[name].required && !dates[name])
}

export function hasMissingTaskDates(dates: TaskDates, config: ResolvedTaskDateConfig): boolean {
    return getMissingTaskDates(dates, config).length > 0
}

/** The dates an overview shows and sorts on, in the order they appear: start before due, and only what takes part. */
export function getVisibleTaskDates(config: ResolvedTaskDateConfig): TaskDateName[] {
    const names: TaskDateName[] = ['start', 'due']

    return names.filter(name => config[name].visible)
}

/**
 * The translation key of a date field. An office that works with both dates names them apart ("Start datum" / "Tot
 * datum"); with a single date the field is simply "Datum", whichever of the two it is (HAM-4286, criterion 1).
 */
export function getTaskDateLabelKey(name: TaskDateName, config: ResolvedTaskDateConfig): string {
    return config.twoDates ? (name === 'start' ? 'task.start' : 'task.due') : 'task.date'
}

export function isZeroDuration(duration?: string): boolean {
    return !duration || parseTimeInMinutes(duration) === 0
}

/**
 * Whether a duration is shown for a single task. A duration of 0:00 - which is what an empty duration means - follows
 * the office setting; any other duration is always shown.
 */
export function isDurationVisible(duration: string | undefined, config: ResolvedTaskDateConfig): boolean {
    return !config.duration.hideZeroValue || !isZeroDuration(duration)
}

/**
 * Lift a duration to the minimum the office set. Used for the default of a new task and when an older task with a
 * shorter duration is edited (HAM-4286, criterion 11).
 */
export function applyMinimumDuration(duration: string | undefined, minimum?: string): string {
    const value = duration || '0:00'
    if (!minimum) {
        return value
    }

    return parseTimeInMinutes(value) < parseTimeInMinutes(minimum) ? minimum : value
}

export function isBelowMinimumDuration(duration: string | undefined, minimum?: string): boolean {
    return !!minimum && parseTimeInMinutes(duration || '0:00') < parseTimeInMinutes(minimum)
}
